import React, { FC, useEffect, useState } from 'react'

import Select from 'react-select'

import { MAX_TEXT_LENGTH, MAX_TITLE_LENGTH, MIN_TITLE_LENGTH } from 'tabs/data/constants'
import { TrainingStatus } from 'tabs/settings/constants/trainingStatus'

import { cloneDeep, isEqual, noop } from 'lodash'
import * as S from './ColumnsForm.style'
import { Prompt } from 'react-router-dom'
import { formatDateTime } from 'helpers/formatDate/formatDate'
import { DatasetRule, MetricTypes } from 'tabs/data/api/generated/models'
import { MetricsTypes } from 'tabs/data/constants/metricsTypes'
import { CreatableMultiselect } from 'tabs/data/components/CreatableMultiselect'
import { DeleteData } from 'tabs/data/components/DeleteData'
import { CButton } from 'tabs/data/components/CButton'
import { CreateDatasetColumnsParams } from 'tabs/data/api/columns'
import { DatasetRules } from '../../DatasetRules'

const initMetricType = (type: MetricTypes): MetricTypes => {
  return MetricsTypes[type] || MetricTypes.Text
}

const MetricOptions: { value: MetricTypes; label: MetricTypes }[] = Object.values(MetricsTypes).map(value => ({
  value,
  label: value,
}))

interface ColumnsFormProps {
  trainingStatus?: string
  inputText?: string
  textAreaContent?: string
  updatedAt?: number

  cancelButtonLabel?: string
  rules: DatasetRule[]
  synonyms: string[]
  type: MetricTypes

  updateCallback?: () => Promise<void>
  onSubmit: (params: CreateDatasetColumnsParams) => Promise<void>
  onCancel?: () => void
  onDelete?: (forceDelete: boolean) => Promise<any>
}

export const ColumnsForm: FC<ColumnsFormProps> = ({
  trainingStatus,
  inputText = '',
  updatedAt,
  textAreaContent = '',
  rules: rulesValue,
  synonyms: synonymsValue,
  type: typeValue,
  cancelButtonLabel,

  updateCallback,
  onSubmit,
  onCancel = noop,
  onDelete,
}) => {
  const [loading, setLoading] = useState(false)

  const [title, setTitle] = useState(inputText || '')
  const [text, setText] = useState(textAreaContent || '')
  const [type, setType] = useState(initMetricType(typeValue))
  const [rules, setRules] = useState(rulesValue || [])
  const [synonyms, setSynonyms] = useState<string[]>(synonymsValue || [])

  const [errorDescription, setErrorDescription] = useState('')
  const [errorTitle, setErrorTitle] = useState('')
  const [errorRule, setRulesError] = useState('')
  const [errorAPI, setErrorAPI] = useState('')

  const formHasChanges =
    title.trim() !== (inputText || '').trim() ||
    text.trim() !== (textAreaContent || '').trim() ||
    type !== typeValue ||
    !isEqual(rules, rulesValue || []) ||
    !isEqual(synonyms, synonymsValue || [])

  const formError = errorTitle || errorDescription || errorRule || errorAPI
  const requiredFieldEmpty = title.trim().length < MIN_TITLE_LENGTH || text.trim().length < 0

  const isSubmitButtonDisabled = requiredFieldEmpty || !formHasChanges || formError

  const handleTitleChange = e => {
    if (errorAPI) setErrorAPI('')

    const { value = '' } = e.target

    if (value.length > MAX_TITLE_LENGTH) {
      setErrorTitle(`You've reached the max length for the text title value — ${MAX_TITLE_LENGTH} characters`)
    } else if (value.trim().length < MIN_TITLE_LENGTH) {
      setErrorTitle(`The title must be at least ${MIN_TITLE_LENGTH} characters long.`)
    } else {
      setErrorTitle('')
    }

    setTitle(e.target.value)
  }

  const handleDescriptionChange = e => {
    if (errorAPI) setErrorAPI('')

    const { value } = e.target

    if (value.length > MAX_TEXT_LENGTH) {
      setErrorDescription(`You've reached the max length for the text value — ${MAX_TEXT_LENGTH} characters`)
    } else if (!value.trim().length) {
      setErrorDescription("The field can't be empty")
    } else {
      setErrorDescription('')
    }

    setText(e.target.value)
  }

  const handleReset = () => {
    setTitle(inputText)
    setText(textAreaContent)
    setSynonyms(cloneDeep(synonymsValue || []))
    setRules(cloneDeep(rulesValue || []))
    setType(initMetricType(typeValue))

    setErrorDescription('')
    setErrorTitle('')
    setRulesError('')
    setErrorAPI('')

    if (onCancel) {
      onCancel()
    }
  }

  const handleSubmit = () => {
    if (loading) return

    try {
      onSubmit({
        name: title.trim(),
        description: text.trim(),
        type: type,
        rules: (rules || []).filter(({ type, rule = '' }) => rule.trim().length && type),
        synonyms: (synonyms || []).filter(s => s.trim().length > 0),
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (formHasChanges) window.onbeforeunload = () => true
    if (!formHasChanges) window.onbeforeunload = () => null

    return () => {
      window.onbeforeunload = null
    }
  }, [formHasChanges, isSubmitButtonDisabled])

  return (
    <S.Container>
      <Prompt
        when={formHasChanges}
        message="Are you sure you want to leave this page without saving your current changes?"
      />
      <S.Form>
        <S.Row>
          <S.RowCell>
            <S.Title>Title*</S.Title>
            <S.InputContainer isError={Boolean(errorTitle)}>
              <S.TitleInput
                disabled={loading}
                onChange={handleTitleChange}
                value={title}
                isError={Boolean(errorTitle)}
                placeholder="Enter title"
              />
            </S.InputContainer>
            <S.Error>{errorTitle}</S.Error>
          </S.RowCell>

          <S.RowCell>
            <S.Title>Type*</S.Title>
            <S.SelectorContainer>
              <Select
                isDisabled={loading}
                styles={S.selectCustomStyles}
                options={MetricOptions}
                value={MetricOptions.find(({ value }) => value === type)}
                onChange={item => {
                  setType(item.value)
                }}
              />
            </S.SelectorContainer>
          </S.RowCell>
        </S.Row>

        <S.Title>Description*</S.Title>
        <S.InputContainer isError={Boolean(errorDescription)}>
          <S.Textarea
            disabled={loading}
            placeholder="+ Insert text here"
            value={text}
            onChange={handleDescriptionChange}
            isError={Boolean(errorDescription)}
          />
        </S.InputContainer>
        <S.Error>{errorDescription}</S.Error>

        <S.Title>
          Synonyms <small>(optional)</small>
        </S.Title>
        <CreatableMultiselect disabled={loading} options={synonymsValue} values={synonyms} onChange={setSynonyms} />

        <DatasetRules rules={rules} onChange={setRules} />

        <S.Footer>
          {updatedAt && (
            <small style={{ opacity: 0.75, marginRight: 'auto' }}>Updated at: {formatDateTime(updatedAt * 1000)}</small>
          )}
          <CButton color="secondary" onClick={handleReset}>
            {cancelButtonLabel || 'Cancel'}
          </CButton>
          <CButton onClick={handleSubmit} disabled={Boolean(loading || isSubmitButtonDisabled)}>
            Save
          </CButton>

          {onDelete && (
            <DeleteData
              buttonLabel="Delete Column"
              subtitle={inputText}
              onDelete={onDelete}
              updateCallback={updateCallback}
            />
          )}
        </S.Footer>
      </S.Form>
      {trainingStatus === TrainingStatus.PENDING && <S.DisableScreen />}
    </S.Container>
  )
}
